/**
 * Concrete replay handlers for the offline queue. Registered once at boot.
 * Each handler must throw on network failure so the queue can retry, and
 * resolve on success (or on a non-retryable error after the toast).
 */
import { supabase } from "@/integrations/supabase/client";
import { registerQueueHandler } from "./offlineQueue";

interface SavedEpisodePayload { user_id: string; episode_id: string; op: "save" | "unsave" }
interface SavedTopicPayload {
  op: "create" | "delete" | "update";
  user_id: string;
  topic_id?: string;
  query?: string;
  updates?: { notify_email?: boolean; is_active?: boolean; query?: string };
}
interface EpisodeNotePayload {
  op: "create" | "update" | "delete";
  user_id: string;
  note_id?: string;
  episode_id?: string;
  content?: string;
  timestamp_seconds?: number | null;
}
interface ResumePositionPayload {
  user_id: string; episode_id: string; position_seconds: number; duration_seconds: number | null;
}
interface PlaylistItemPayload {
  op: "add" | "remove";
  playlist_id: string;
  episode_id: string;
}

function throwIfNetwork(error: unknown) {
  // Supabase JS returns `{ error }` for HTTP errors. Treat fetch-layer errors
  // as retryable; 4xx/5xx responses bubble as `.error` with a code — we drop
  // those by returning successfully (the optimistic UI already moved on).
  const msg = (error as { message?: string })?.message?.toLowerCase() || "";
  if (
    error instanceof TypeError ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  ) {
    throw error;
  }
}

export function registerAllQueueHandlers() {
  registerQueueHandler("saved_episode", async (raw) => {
    const p = raw as SavedEpisodePayload;
    try {
      if (p.op === "save") {
        const { error } = await supabase.from("saved_episodes").insert({ user_id: p.user_id, episode_id: p.episode_id });
        if (error && error.code !== "23505") throwIfNetwork(error);
      } else {
        const { error } = await supabase.from("saved_episodes").delete().eq("user_id", p.user_id).eq("episode_id", p.episode_id);
        if (error) throwIfNetwork(error);
      }
    } catch (e) { throwIfNetwork(e); throw e; }
  });

  registerQueueHandler("saved_topic", async (raw) => {
    const p = raw as SavedTopicPayload;
    try {
      if (p.op === "create" && p.query) {
        const { error } = await supabase.from("saved_topics").insert({ user_id: p.user_id, query: p.query });
        if (error) throwIfNetwork(error);
      } else if (p.op === "delete" && p.topic_id) {
        const { error } = await supabase.from("saved_topics").delete().eq("id", p.topic_id);
        if (error) throwIfNetwork(error);
      } else if (p.op === "update" && p.topic_id && p.updates) {
        const { error } = await supabase.from("saved_topics").update(p.updates).eq("id", p.topic_id);
        if (error) throwIfNetwork(error);
      }
    } catch (e) { throwIfNetwork(e); throw e; }
  });

  registerQueueHandler("episode_note", async (raw) => {
    const p = raw as EpisodeNotePayload;
    try {
      if (p.op === "create" && p.episode_id && p.content !== undefined) {
        const { error } = await supabase.from("episode_notes").insert({
          user_id: p.user_id,
          episode_id: p.episode_id,
          content: p.content,
          timestamp_seconds: p.timestamp_seconds ?? null,
        });
        if (error) throwIfNetwork(error);
      } else if (p.op === "update" && p.note_id && p.content !== undefined) {
        const updates: { content: string; timestamp_seconds?: number | null } = { content: p.content };
        if (p.timestamp_seconds !== undefined) updates.timestamp_seconds = p.timestamp_seconds;
        const { error } = await supabase.from("episode_notes").update(updates).eq("id", p.note_id);
        if (error) throwIfNetwork(error);
      } else if (p.op === "delete" && p.note_id) {
        const { error } = await supabase.from("episode_notes").delete().eq("id", p.note_id);
        if (error) throwIfNetwork(error);
      }
    } catch (e) { throwIfNetwork(e); throw e; }
  });

  registerQueueHandler("resume_position", async (raw) => {
    const p = raw as ResumePositionPayload;
    try {
      const { error } = await supabase.from("episode_resume_positions").upsert(
        {
          user_id: p.user_id,
          episode_id: p.episode_id,
          position_seconds: p.position_seconds,
          duration_seconds: p.duration_seconds,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,episode_id" },
      );
      if (error) throwIfNetwork(error);
    } catch (e) { throwIfNetwork(e); throw e; }
  });

  registerQueueHandler("playlist_item", async (raw) => {
    const p = raw as PlaylistItemPayload;
    try {
      if (p.op === "add") {
        const { error } = await supabase.from("playlist_items").insert({ playlist_id: p.playlist_id, episode_id: p.episode_id });
        if (error && error.code !== "23505") throwIfNetwork(error);
      } else {
        const { error } = await supabase.from("playlist_items").delete().eq("playlist_id", p.playlist_id).eq("episode_id", p.episode_id);
        if (error) throwIfNetwork(error);
      }
    } catch (e) { throwIfNetwork(e); throw e; }
  });
}
